import type { Instant } from '@js-joda/core';
import type { AddProviderInstrumentRequest, ProviderInstrumentConfig } from '@thinkalpha/locates-ws-client';
import type { ProviderInstruments } from 'src/admin/LAB/contracts/providers';
import type { SuspendedUntilDraft } from 'src/admin/LAB/SuperUserLAB/ProviderConfiguration/suspensionWindow';
import {
    isSuspensionInEffect,
    resolveSuspendedUntilDraft,
} from 'src/admin/LAB/SuperUserLAB/ProviderConfiguration/suspensionWindow';

/** One `instruments` entry with its map key attached, for list rows and dialogs. */
export interface ProviderInstrumentRow extends ProviderInstrumentConfig {
    readonly symbol: string;
}

/** A list row with the suspension judged against the current time. */
export interface ProviderInstrumentListRow extends ProviderInstrumentRow {
    /** False once the entry lapsed: recorded before the business day began, or its end time passed. */
    readonly inEffect: boolean;
}

export interface InstrumentDraft {
    /** Free text; normalized with {@link normalizeInstrumentSymbol} when sent. */
    readonly symbol: string;
    readonly reason: string;
    /** Optional end time as a `datetime-local` value in the browser's zone; `''` for none. */
    readonly suspendedUntil: string;
}

export interface InstrumentDialogState {
    readonly mode: 'add' | 'edit';
    /** The stored entry being edited; `null` in add mode. */
    readonly original: ProviderInstrumentRow | null;
    readonly draft: InstrumentDraft;
    /** `form` collects the entry; `confirm` shows what will be written before sending. */
    readonly step: 'form' | 'confirm';
    /** True while the write is in flight. */
    readonly isSaving: boolean;
    /** Identifies the in-flight write so a stale completion can't touch a newer dialog; `null` when idle. */
    readonly saveToken: number | null;
    /** Message of the last failed write; cleared when a new write starts. */
    readonly error: string | null;
    /** True once the end-time field was edited; an untouched field follows the stored entry on refresh. */
    readonly endTimeTouched: boolean;
}

export interface PendingInstrumentRemovalState {
    readonly entry: ProviderInstrumentRow;
    /** Identifies this confirmation so a stale completion can't clear a newer one. */
    readonly token: number;
    readonly isRemoving: boolean;
    readonly error: string | null;
}

export interface ProviderInstrumentsState {
    readonly providerId: string | null;
    readonly instruments: ProviderInstruments;
    readonly dialog: InstrumentDialogState | null;
    readonly pendingRemoval: PendingInstrumentRemovalState | null;
}

export const initialProviderInstrumentsState: ProviderInstrumentsState = {
    providerId: null,
    instruments: {},
    dialog: null,
    pendingRemoval: null,
};

export const EMPTY_INSTRUMENT_DRAFT: InstrumentDraft = {
    symbol: '',
    reason: '',
    suspendedUntil: '',
};

/** Matches the backend's instrument key: trimmed and upper-cased. */
export function normalizeInstrumentSymbol(symbol: string): string {
    return symbol.trim().toUpperCase();
}

/** Rows sorted by symbol so the list order is stable across writes. */
export function instrumentRows(instruments: ProviderInstruments): ProviderInstrumentRow[] {
    return Object.entries(instruments)
        .map(([symbol, entry]) => ({ ...entry, symbol }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/** The same rows with each suspension judged against `now`. */
export function instrumentListRows(rows: readonly ProviderInstrumentRow[], now: Instant): ProviderInstrumentListRow[] {
    return rows.map((row) => ({ ...row, inEffect: isSuspensionInEffect(row, now) }));
}

/** Whether the stored end time and the drafted one are the same. A stored end time that already passed pre-fills blank, so a blank draft leaves it as is. */
function sameEndTime(stored: Instant | undefined, draft: SuspendedUntilDraft, now: Instant): boolean {
    if (draft.kind === 'none') return stored === undefined || !stored.isAfter(now);
    if (draft.kind === 'ok') return stored !== undefined && stored.equals(draft.value);
    return false;
}

/** The draft normalized for the API, or `null` when it is not ready to send. */
export function parseInstrumentDraft(dialog: InstrumentDialogState, now: Instant): AddProviderInstrumentRequest | null {
    const symbol =
        dialog.mode === 'edit' && dialog.original
            ? dialog.original.symbol
            : normalizeInstrumentSymbol(dialog.draft.symbol);
    const reason = dialog.draft.reason.trim();
    if (!symbol || !reason) return null;
    const endTime = resolveSuspendedUntilDraft(dialog.draft.suspendedUntil, dialog.original?.suspendedUntil, now);
    if (endTime.kind === 'problem') return null;
    // An edit that changes neither the reason nor the end time has nothing to write.
    if (
        dialog.mode === 'edit' &&
        dialog.original?.reason === reason &&
        sameEndTime(dialog.original.suspendedUntil, endTime, now)
    ) {
        return null;
    }
    return { symbol, reason, ...(endTime.kind === 'ok' && { suspendedUntil: endTime.value.toString() }) };
}
